/**
 * Route path constants and parsers for expert-review URLs.
 *
 * Belongs here: EXPERT_REVIEW_ROUTES, parseStrategyStageIdFromPath, parsePreferenceTypeIdFromPath.
 * Does not belong here: navigation side effects (see expertReviewNavigation.tsx).
 */

export const EXPERT_REVIEW_ROUTE_SEGMENTS = {
  review: 'review',
  strategy: {
    root: 'strategy-questionnaire',
    riskProfile: 'risk-profile',
    review: 'review',
    stageIdParam: ':stageId'
  },
  preferences: {
    root: 'preferences',
    questionnaire: 'questionnaire',
    type: 'type',
    typeWithParam: 'type/:preferenceTypeId',
    review: 'review'
  }
} as const

const EXPERT_REVIEW_ROOT = '/allocation/expert-review'
const STRATEGY_ROOT = `${EXPERT_REVIEW_ROOT}/${EXPERT_REVIEW_ROUTE_SEGMENTS.strategy.root}`
const PREFERENCES_ROOT = `${EXPERT_REVIEW_ROOT}/${EXPERT_REVIEW_ROUTE_SEGMENTS.preferences.root}`

export const EXPERT_REVIEW_ROUTES = {
  root: EXPERT_REVIEW_ROOT,
  review: `${EXPERT_REVIEW_ROOT}/${EXPERT_REVIEW_ROUTE_SEGMENTS.review}`,
  strategy: {
    root: STRATEGY_ROOT,
    review: `${STRATEGY_ROOT}/${EXPERT_REVIEW_ROUTE_SEGMENTS.strategy.review}`,
    riskProfile: `${STRATEGY_ROOT}/${EXPERT_REVIEW_ROUTE_SEGMENTS.strategy.riskProfile}`,
    editStage: (stageId: string) => `${STRATEGY_ROOT}/${stageId}`
  },
  preferences: {
    list: PREFERENCES_ROOT,
    questionnaire: `${PREFERENCES_ROOT}/${EXPERT_REVIEW_ROUTE_SEGMENTS.preferences.questionnaire}`,
    review: `${PREFERENCES_ROOT}/${EXPERT_REVIEW_ROUTE_SEGMENTS.preferences.review}`,
    editPreferenceType: (preferenceTypeId: string) =>
      `${PREFERENCES_ROOT}/${EXPERT_REVIEW_ROUTE_SEGMENTS.preferences.type}/${preferenceTypeId}`
  }
} as const

/** True on hub index (`/allocation/expert-review`), not nested questionnaire routes. */
export function isExpertReviewHubPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, '') || '/'
  return normalized === EXPERT_REVIEW_ROUTES.root
}

const STRATEGY_NON_STAGE_SEGMENTS = new Set(['review', 'risk-profile'])

/** Last path segment when on `/strategy-questionnaire/:stageId` (edit overlay). */
export function parseStrategyStageIdFromPath(pathname: string): string | undefined {
  const strategyRoot = EXPERT_REVIEW_ROUTES.strategy.root
  if (!pathname.startsWith(strategyRoot)) return undefined
  const segments = pathname.split('/').filter(Boolean)
  const lastSegment = segments[segments.length - 1]
  if (!lastSegment || lastSegment === 'strategy-questionnaire') return undefined
  if (STRATEGY_NON_STAGE_SEGMENTS.has(lastSegment)) return undefined
  return lastSegment
}

/** Preference type id from `/preferences/type/:preferenceTypeId`. */
export function parsePreferenceTypeIdFromPath(pathname: string): string | undefined {
  const typePathPrefix = `${EXPERT_REVIEW_ROUTES.preferences.list}/type/`
  if (!pathname.includes(typePathPrefix)) return undefined
  const afterPrefix = pathname.split(typePathPrefix)[1]
  if (!afterPrefix) return undefined
  const preferenceTypeId = afterPrefix.split('/')[0]?.trim()
  return preferenceTypeId || undefined
}
